import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { v4 as uuidv4 } from 'uuid'
import PropTypes from 'prop-types'

import List from '../../../../features/components/List'
import PageSection from '../../../../features/components/PageSection'
import ItemsLoading from '../../../../components/Loaders/ItemsLoading'
import SkeletonListRow from '../../../../features/components/List/Skeleton'
import ActionsDropdown from '../../../../features/components/ActionsDropdown'

import { deleteAccessibleSocialPages } from '../../../../modules/actions/socialAccounts'
import {
  accessibleSocialPagesSelector,
  accessibleSocialPagesWasLoadedSelector,
  accessibleSocialPagesErrorSelector,
  accessibleSocialPagesIsLoadingSelector,
  deleteAccessibleSocialPagesIdSelector,
  deleteAccessibleSocialPagesIsLoadingSelector
} from '../../../../modules/selectors/socialAccounts'

import useCommonStyles from '../../../../styles/common/listPage'
import useStyles from './styles'

const AccessibleSocialPagesList = React.memo(({ platformProvider }) => {
  const commonClasses = useCommonStyles()
  const classes = useStyles()

  const dispatch = useDispatch()

  const socialPages = useSelector(accessibleSocialPagesSelector)
  // filtered list based on platform
  const pagesToShow = socialPages.filter(({ platform }) => platform === platformProvider)

  const socialPageIdDeleting = useSelector(deleteAccessibleSocialPagesIdSelector)
  const socialPageIsDeleting = useSelector(deleteAccessibleSocialPagesIsLoadingSelector)

  const socialPageDelete = useCallback(id => dispatch(deleteAccessibleSocialPages(id)), [dispatch])

  const getDropdownOptions = useCallback(
    id => [
      {
        text: 'Delete',
        onClickHandler: () => socialPageDelete(id)
      }
    ],
    [socialPageDelete]
  )

  const itemsListColumns = useMemo(
    () => [
      {
        Component: ({ thumbnail }) => (
          <img
            className={classes.listImage}
            src={thumbnail}
            alt="icon"
            onError={i => (i.target.style.display = 'none')}
          />
        ),
        style: {
          maxWidth: 50,
          padding: '0 5px 0 0'
        }
      },
      {
        headTitle: 'Name',
        field: 'name'
      },
      {
        headTitle: 'Page ID',
        field: 'external_id'
      },
      {
        Component: ({ id }) => <ActionsDropdown options={getDropdownOptions(id)} />,
        style: {
          maxWidth: 30
        }
      }
    ],
    [classes.listImage, getDropdownOptions]
  )

  const listSkeletonProps = useMemo(() => ({ cols: itemsListColumns }), [itemsListColumns])

  return (
    <ItemsLoading
      className={commonClasses.itemList}
      itemsLength={pagesToShow.length}
      SkeletonComponent={SkeletonListRow}
      skeletonProps={listSkeletonProps}
      errorSelector={accessibleSocialPagesErrorSelector}
      isLoadingSelector={accessibleSocialPagesIsLoadingSelector}
      wasLoadedSelector={accessibleSocialPagesWasLoadedSelector}
      ContentWrapper={PageSection}
      isEmptyNoDataMessage
    >
      <List
        cols={itemsListColumns}
        listData={pagesToShow}
        itemDeletingId={socialPageIsDeleting ? socialPageIdDeleting : ''}
        Skeleton={<SkeletonListRow {...listSkeletonProps} key={uuidv4()} />}
      />
    </ItemsLoading>
  )
})

AccessibleSocialPagesList.propTypes = {
  platformProvider: PropTypes.string.isRequired
}
export default AccessibleSocialPagesList
